import type { Component } from 'solid-js'
import type { DivProps } from '../../ui/utils-ui'
import { filterAndMap, lastWriteWins } from '@wovin/core/query'
import { Logger } from 'besonders-logger'
import { createEffect, createMemo, createSignal, Show, splitProps, untrack } from 'solid-js'
import { getMnemonicFromIDB, initializeCryptoKeypairs } from '../../data/agent/AgentCrypto'
import { boundInput, useAgent } from '../../data/agent/AgentState'
import { getApplogDB } from '../../data/ApplogDB'
import { copyToClipboard } from '../../data/block-ui-helpers-paste'
import { notifyToast } from '../../ui/utils-ui'
import { Iconify } from '../mini-components'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.DEBUG) // eslint-disable-line unused-imports/no-unused-vars
const persist = ['username', 'devicename', 'agentcode'] // will sync to localStorage

const buttonStyle = {
	// '--wa-spacing-small': '6px',
}
export const AgentEditor: Component<DivProps & { initialSetup?: boolean, onSave?: () => void }> = (allProps) => {
	const [props, restProps] = splitProps(allProps, ['initialSetup', 'onSave'])
	const agent = useAgent()
	const originalAg = agent.expectedAgentHash
	const [dirty, setDirty] = createSignal(false)
	const dirtyOrInitial = createMemo(() => props.initialSetup || dirty())
	createEffect(() => {
		DEBUG(`Updating dirty flag from`, untrack(() => dirty()), 'to', !agent.isAgentStringMatching, {
			dbLogs: filterAndMap(lastWriteWins(getApplogDB()), { en: agent.ag, at: 'agent/appAgent' }, 'vl'),
		})
		setDirty(!agent.isAgentStringMatching)
	})
	const onChange = (_newVal) => {
		DEBUG(`Changed, setting dirty`, { before: dirty() })
		setDirty(true)
	}

	const onSave = async () => {
		// agent.ensureAgentAtoms() // recycle the old mnemonic and write the new agent situation to idb and atoms
		const { ag, expectedAgentHash } = agent
		DEBUG({ ag, expectedAgentHash, originalAg })
		const existingMnemonicToRecycle = await getMnemonicFromIDB(agent, originalAg)
		if (!existingMnemonicToRecycle) {
			const toastMsg = 'Missing mneumonic import one to alter your agent string'
			notifyToast(toastMsg)
			throw ERROR('saving a new agent string requires a mnemonic', { ag, expectedAgentHash, originalAg })
		}
		const cleanedMnemString = existingMnemonicToRecycle.replaceAll(' ', ',').replaceAll(`\n`, ',').replaceAll(',,,', ',').replaceAll(',,', ',')
		const cleanedMnemArray = cleanedMnemString.split(',')
		DEBUG({ cleanedMnemArray })
		const expectedAgentHashBeforeInitialize = expectedAgentHash
		initializeCryptoKeypairs(agent, cleanedMnemArray, true) // recycle the previous mnemonic and skip the warning
		props.onSave?.()
		if (props.initialSetup) {
			WARN({ expectedAgentHashBeforeInitialize, expectedAgentHash }, '//TODO consider cleaning up IDB if this was an initial setup save')
		}
		setDirty(false)
	}
	const AgentStringPatternCard = () => (
		<div text-block text='xs md:sm lg:sm' class='border-1 border-purple-300 rounded-md border-solid bg-purple-50/50 p-4'>
			<div>
				The Pattern for agent string:
			</div>
			<div>
				chosenUsername.[generatedDID]@[fixedAppName].chosenAgentName.chosenMachineName
			</div>
		</div>
	)
	const saveButtonLabel = () => props.initialSetup && !dirty() ? 'Continue as \'demouser\'' : 'Save'
	return (
		<div w-full flex='~ wrap items-center gap-4 gap-y-2' box-border py-1 {...restProps}>

			<div overflow-auto text='xs md:base lg:lg'>
				{boundInput('username', persist, {}, onChange /* , !!props.initialSetup */)}
				.
				<span title={`The end of your DID:\n${agent.did}`} cursor-help>{agent.shortDID}</span>
				@
				{
					agent.app //
				}
				.
				{
					boundInput('agentcode', persist, {}, onChange) //
				}
				.
				{
					boundInput('devicename', persist, {}, onChange) //
				}
				<Show when={dirtyOrInitial()}>
					<wa-button
						style={buttonStyle}
						size='s'
						class='mx-4'
						variant={dirtyOrInitial() ? 'success' : 'primary'}
						// outline={dirty()}
						onClick={onSave}
					>
						<Iconify
							size={4}
							name={!dirtyOrInitial() ? 'checks-bold' : 'floppy-disk'}
							slot='start'
						/>
						{saveButtonLabel()}
					</wa-button>
				</Show>
				<wa-button
					mx-4
					style={buttonStyle}
					size='s'
					title='Copy agent string'
					onClick={() => {
						copyToClipboard(agent.agentString)
					}}
				>
					<Iconify name='copy-light' size={4} slot='start' />
					Copy Agent String
				</wa-button>
				<wa-button

					style={buttonStyle}
					size='s'
					title={agent.did}
					onClick={() => {
						copyToClipboard(agent.did)
					}}
				>
					<Iconify name='copy-light' size={4} slot='start' />
					Copy Agent DID
				</wa-button>
			</div>
			<AgentStringPatternCard />

		</div>
	)
}
